import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Landmark,
  CheckCircle2,
  Wallet,
  CreditCard,
  BriefcaseBusiness,
  MapPin,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Info,
  Eye,
  EyeOff,
  ChevronsDown,
  ChevronsUp,
} from "lucide-react";
import { api, money } from "../lib/api.js";
import { Card, Modal, Pill, Empty, Notice } from "../components/UI.jsx";
import CurrencySelect from "../components/CurrencySelect.jsx";
const iconFor = (t) =>
  t === "cash"
    ? Wallet
    : t === "credit_card"
      ? CreditCard
      : t === "investment"
        ? BriefcaseBusiness
        : Landmark;
const ACCOUNT_TYPES = [
  ["checking", "Current / checking", "Everyday money and payments"],
  ["savings", "Savings", "Cash saved at a bank"],
  ["cash", "Cash", "Physical cash or petty cash"],
  ["credit_card", "Credit card", "Money owed on a card"],
  ["loan", "Loan", "A loan balance you owe"],
  ["mortgage", "Mortgage", "Property-backed money owed"],
  ["other", "Other money account", "Another account that holds money or debt"],
];
const accountTypeLabel = (value) => ACCOUNT_TYPES.find(([key]) => key === value)?.[1] || String(value || "").replaceAll("_", " ");
export default function Accounts() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const currencyFilter = String(params.get("currency") || "").toUpperCase();
  const [a, setA] = useState([]),
    [cfg, setCfg] = useState(null),
    [dash, setDash] = useState(null),
    [fx, setFx] = useState(null),
    [modal, setModal] = useState(false),
    [accountStep, setAccountStep] = useState(1),
    [accountMore, setAccountMore] = useState(false),
    [wizardError, setWizardError] = useState(""),
    [error, setError] = useState(""),
    [recycle, setRecycle] = useState([]),
    [selected, setSelected] = useState(new Set()),
    [collapsed, setCollapsed] = useState(new Set()),
    [showHidden, setShowHidden] = useState(false);
  const [f, setF] = useState({
    name: "",
    accountType: "checking",
    currency: "EUR",
    identifier: "",
    openingBalance: 0,
    costCenterId: "",
    locationId: "",
    newLocationName: "",
    newLocationType: "bank",
    includeInTotals: true,
    isLiability: false,
  });
  const load = () =>
    Promise.all([
      api("/accounts"),
      api("/config"),
      api("/dashboard?period=month").catch(() => null),
      api("/accounts-recycle-bin").catch(() => []),
      api("/fx/latest").catch(() => null),
    ])
      .then(([x, c, d, bin, rates]) => {
        setA(x);
        setCfg(c);
        setDash(d);
        setRecycle(bin);
        setFx(rates);
      })
      .catch((e) => setError(e.message));
  useEffect(() => {
    load();
  }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      let locationId = f.locationId;
      if (locationId === "new") {
        if (!f.newLocationName.trim())
          throw new Error("Give the new place a name.");
        const r = await api("/locations", {
          method: "POST",
          body: {
            name: f.newLocationName.trim(),
            locationType: f.newLocationType,
            icon: f.newLocationType,
            costCenterId: f.costCenterId || null,
          },
        });
        locationId = String(r.id);
      }
      const created = await api("/accounts", {
        method: "POST",
        body: {
          ...f,
          locationId: locationId || null,
          costCenterId: f.costCenterId || null,
        },
      });
      const createdType = f.accountType;
      setModal(false);
      setWizardError("");
      setAccountStep(1);
      setAccountMore(false);
      setF({
        name: "",
        accountType: "checking",
        currency: "EUR",
        identifier: "",
        openingBalance: 0,
        costCenterId: "",
        locationId: "",
        newLocationName: "",
        newLocationType: "bank",
        includeInTotals: true,
        isLiability: false,
      });
      await load();
      if (createdType === "investment" && created?.id) nav(`/accounts/${created.id}`);
    } catch (e) {
      if (modal) {
        setWizardError(e.message);
        if (/account named/i.test(e.message)) setAccountStep(4);
        else if (/place named/i.test(e.message)) setAccountStep(2);
      } else setError(e.message);
    }
  };
  const costKeys = useMemo(() => {
    const m = new Map();
    for (const x of a) {
      const key = x.is_shared
        ? `shared:${x.owner_user_id}`
        : `cc:${x.cost_center_id || "none"}`;
      if (!m.has(key))
        m.set(
          key,
          x.is_shared
            ? `${x.owner_name || "Shared"}'s Accounts`
            : x.cost_center_name || "Unassigned",
        );
    }
    return [...m.entries()];
  }, [a]);
  const visible = useMemo(
    () =>
      selected.size
        ? a.filter((x) => Number(x.hide_on_accounts_page || 0) === 0 &&
            (!currencyFilter || x.currency === currencyFilter) && selected.has(
              x.is_shared
                ? `shared:${x.owner_user_id}`
                : `cc:${x.cost_center_id || "none"}`,
            ),
          )
        : a.filter((x) => Number(x.hide_on_accounts_page || 0) === 0 && (!currencyFilter || x.currency === currencyFilter)),
    [a, selected, currencyFilter],
  );
  const locations = useMemo(() => {
    const m = new Map();
    for (const x of visible) {
      const key = x.location_id
        ? `loc:${x.location_id}`
        : `none:${x.is_shared ? x.owner_user_id : "own"}`;
      const name = x.location_name || x.bank_name || "Unassigned place";
      if (!m.has(key)) m.set(key, { name, items: [] });
      m.get(key).items.push(x);
    }
    return [...m.entries()];
  }, [visible]);
  const total = dash?.totalBalance ?? 0,
    totalCurrency = dash?.baseCurrency || cfg?.userBaseCurrency || "EUR";
  const signedBalance = (x) => x.is_liability ? -Math.abs(Number(x.current_balance || 0)) : Number(x.current_balance || 0);
  const convertToReport = (value, currency) => {
    if (currency === totalCurrency) return value;
    const rates = Object.fromEntries((fx?.rates || []).map((r) => [r.currency, Number(r.eur_rate)]));
    rates.EUR = 1;
    return rates[currency] && rates[totalCurrency] ? value / rates[currency] * rates[totalCurrency] : null;
  };
  const placeTotals = (items) => {
    const included = items.filter((x) => x.is_active !== 0 && x.include_in_totals !== 0);
    const native = new Map();
    included.forEach((x) => native.set(x.currency, (native.get(x.currency) || 0) + signedBalance(x)));
    const converted = included.reduce((sum, x) => {
      const value = convertToReport(signedBalance(x), x.currency);
      return value == null ? sum : sum + value;
    }, 0);
    return { native: [...native.entries()], converted };
  };
  const toggleFilter = (k) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  const toggleLoc = (k) =>
    setCollapsed((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  const hiddenAccounts = useMemo(
    () => a.filter((x) => Number(x.hide_on_accounts_page || 0) === 1),
    [a],
  );
  const setAccountHidden = async (account, hidden) => {
    try {
      await api(`/accounts/${account.id}/visibility`, {
        method: "PATCH",
        body: { hidden },
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const restore = async (item) => {
    try {
      await api(`/accounts-recycle-bin/${item.id}/restore`, {
        method: "POST",
        body: {},
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const closeAccountWizard = () => {
    setModal(false);
    setAccountStep(1);
    setAccountMore(false);
    setWizardError("");
  };
  const chooseLocation = (locationId) => {
    const loc = cfg.locations.find((x) => String(x.id) === String(locationId));
    setF({
      ...f,
      locationId: String(locationId),
      costCenterId: loc?.default_cost_center_id
        ? String(loc.default_cost_center_id)
        : f.costCenterId,
    });
    if (String(locationId) !== "new") setAccountStep(3);
  };
  const nextAccountStep = async () => {
    setWizardError("");
    try {
      if (accountStep === 2 && f.locationId === "new") {
        if (!f.newLocationName.trim())
          throw new Error("Enter a name for the new place.");
        const result = await api(
          `/locations/name-available?name=${encodeURIComponent(f.newLocationName)}`,
        );
        if (!result.available)
          throw new Error(
            `A place named “${f.newLocationName.trim()}” already exists. Choose it above or use a different name.`,
          );
      }
      if (accountStep === 4) {
        if (!f.name.trim()) throw new Error("Enter an account name.");
        const result = await api(
          `/accounts/name-available?name=${encodeURIComponent(f.name)}`,
        );
        if (!result.available)
          throw new Error(
            `An account named “${f.name.trim()}” already exists. Choose a different name.`,
          );
      }
      setAccountStep((x) => Math.min(5, x + 1));
    } catch (e) {
      setWizardError(e.message);
    }
  };
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Accounts</h1>
          <p>Filter by cost center, then browse places and accounts.</p>
        </div>
        <button className="primary" onClick={() => { setAccountStep(1); setModal(true); }}>
          <Plus size={17} /> Add account
        </button>
      </div>
      {error && <div className="error">{error}</div>}
      <Card className="accounts-total">
        <span>Included account total</span>
        <strong>{money(total, totalCurrency)}</strong>
        <small>
          Only active accounts configured to count toward your totals.
        </small>
      </Card>
      <Card className="accounts-browse-controls">
      <div className="cost-filter-row">
        {currencyFilter && <button className="filter-tab active" onClick={()=>nav('/accounts')}>{currencyFilter} accounts ×</button>}
        <button
          className={selected.size === 0 ? "filter-tab active" : "filter-tab"}
          onClick={() => setSelected(new Set())}
        >
          All
        </button>
        {costKeys.map(([k, n]) => (
          <button
            key={k}
            className={selected.has(k) ? "filter-tab active" : "filter-tab"}
            onClick={() => toggleFilter(k)}
          >
            {n}
          </button>
        ))}
        <span className="accounts-control-spacer" />
        <button className="secondary compact" onClick={() => setCollapsed(new Set())}>
          <ChevronsDown size={15} /> Expand all
        </button>
        <button className="secondary compact" onClick={() => setCollapsed(new Set(locations.map(([key]) => key)))}>
          <ChevronsUp size={15} /> Collapse all
        </button>
        <button className="secondary compact" onClick={() => setShowHidden(!showHidden)}>
          <Eye size={15} /> Hidden accounts ({hiddenAccounts.length})
        </button>
      </div>
      {showHidden && (
        <div className="hidden-account-manager">
          <b>Hidden account cards</b>
          <small>These accounts and all their movements still count everywhere else in Pisto.</small>
          {!hiddenAccounts.length ? <span className="muted">No hidden accounts.</span> : hiddenAccounts.map((account) => (
            <button className="filter-pill" key={account.id} onClick={() => setAccountHidden(account, false)}>
              <Eye size={14} /> Show {account.name}
            </button>
          ))}
        </div>
      )}
      </Card>
      {!visible.length ? (
        <Card>
          <Empty title="No accounts in this filter" />
        </Card>
      ) : (
        <div className="place-list">
          {locations.map(([key, { name, items }]) => {
            const place = placeTotals(items);
            return (
            <section className="place-section" key={key}>
              <button className="place-heading" onClick={() => toggleLoc(key)}>
                {collapsed.has(key) ? <ChevronRight /> : <ChevronDown />}
                <MapPin size={17} />
                <strong>{name}</strong>
                <span className="place-native-totals">— {place.native.map(([currency,value]) => money(value,currency)).join(" · ") || "No included balance"}</span>
                <small className="place-report-total" title={fx?.date ? `Converted using the latest available rates (${fx.date}).` : "Converted to your reporting currency."}>Total place balance: {money(place.converted,totalCurrency)}</small>
                <Pill>{items.length}</Pill>
              </button>
              {!collapsed.has(key) && (
                <div className="account-grid">
                  {items.map((x) => {
                    const I = iconFor(x.account_type);
                    return (
                      <Card
                        key={x.id}
                        className={
                          "account-card clickable " +
                          (x.is_active === 0 ? "inactive" : "")
                        }
                        onClick={() => nav(`/accounts/${x.id}`)}
                      >
                        {!x.is_shared && (
                          <button
                            className="icon account-card-hide"
                            title="Hide this account card"
                            aria-label={`Hide ${x.name} from the Accounts page`}
                            onClick={(event) => {
                              event.stopPropagation();
                              setAccountHidden(x, true);
                            }}
                          >
                            <EyeOff size={16} />
                          </button>
                        )}
                        <div className="account-icon">
                          <I />
                        </div>
                        <div>
                          <small>
                            {x.cost_center_name ||
                              (x.is_shared
                                ? `${x.owner_name}'s Accounts`
                                : "Unassigned")}{" "}
                          · {accountTypeLabel(x.account_type)}
                          </small>
                          <h3>{x.name}</h3>
                        </div>
                        <strong>{money(x.current_balance, x.currency)}</strong>
                        <div>
                          <Pill tone={x.is_active ? "green" : "red"}>
                            <CheckCircle2 size={13} />{" "}
                            {x.is_active ? "Active" : "Inactive"}
                          </Pill>{" "}
                          <Pill>{x.currency}</Pill>
                          <Pill tone={x.include_in_totals !== 0 ? "green" : ""}>{x.include_in_totals !== 0 ? "Included in totals" : "Excluded from totals"}</Pill>
                          {x.location_default_cost_center_id &&
                            String(x.location_default_cost_center_id) !==
                              String(x.cost_center_id || "") && (
                              <Pill
                                tone="yellow"
                                title="This account cost center does not match the default cost center configured for its place. Review the account or place settings."
                              >
                                <Info size={12} /> Cost center mismatch
                              </Pill>
                            )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </section>
          )})}
        </div>
      )}
      {recycle.length > 0 && (
        <Card>
          <h2>Recover deleted accounts</h2>
          <p className="muted">
            Encrypted recovery copies expire automatically after 30 days.
          </p>
          <div className="simple-list">
            {recycle.map((x) => (
              <div key={x.id}>
                <span>
                  <b>{x.account_name}</b>
                  <small>
                    Available until{" "}
                    {new Date(x.expires_at).toLocaleDateString()}
                  </small>
                </span>
                <button
                  className="secondary compact"
                  onClick={() => restore(x)}
                >
                  Restore closed account
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}
      {modal && cfg && (
        <Modal
          title={`Add account · ${accountStep} of 5`}
          screenId="D0111"
          onClose={closeAccountWizard}
        >
          <form onSubmit={save} className="account-wizard">
            {wizardError && <Notice tone="error">{wizardError}</Notice>}
            {accountStep === 1 && <section><h3>Who is this account for?</h3><p className="muted">Choose the cost center. Selecting one continues automatically.</p><div className="wizard-choice-grid">{cfg.costCenters.filter(x=>x.is_active!==0).map(x=><button type="button" key={x.id} className={String(f.costCenterId)===String(x.id)?'choice-card active':'choice-card'} onClick={()=>{setF({...f,costCenterId:String(x.id)});setAccountStep(2)}}><BriefcaseBusiness/><b>{x.name}</b><small>{x.center_type}</small></button>)}<button type="button" className={!f.costCenterId?'choice-card active':'choice-card'} onClick={()=>{setF({...f,costCenterId:''});setAccountStep(2)}}><Wallet/><b>Not assigned</b><small>Choose later</small></button></div></section>}
            {accountStep === 2 && <section><h3>Where is the account?</h3><p className="muted">Choose a bank, broker, wallet or other place.</p><div className="wizard-choice-grid">{(cfg.locations||[]).filter(x=>x.is_active!==0).map(x=><button type="button" key={x.id} className={String(f.locationId)===String(x.id)?'choice-card active':'choice-card'} onClick={()=>chooseLocation(x.id)}><MapPin/><b>{x.name}</b><small>{x.location_type.replaceAll('_',' ')}</small></button>)}<button type="button" className={f.locationId==='new'?'choice-card active':'choice-card'} onClick={()=>setF({...f,locationId:'new'})}><Plus/><b>Create a new place</b><small>Bank, broker, wallet or other</small></button></div>{f.locationId==='new'&&<div className="form-grid wizard-new-place"><label>Place name<input autoFocus required value={f.newLocationName} onChange={e=>setF({...f,newLocationName:e.target.value})} placeholder="e.g. CaixaBank or IBKR"/></label><label>Place type<select value={f.newLocationType} onChange={e=>setF({...f,newLocationType:e.target.value})}>{['bank','broker','cash','safe','wallet','other'].map(x=><option key={x} value={x}>{x.replaceAll('_',' ')}</option>)}</select></label></div>}</section>}
            {accountStep === 3 && <section><h3>Which currency does it use?</h3><div className="currency-choice-grid">{['EUR','USD','GBP','ARS','CHF','BRL'].map(currency=><button type="button" className={f.currency===currency?'currency-choice active':'currency-choice'} key={currency} onClick={()=>{setF({...f,currency});setAccountStep(4)}}><b>{currency}</b></button>)}</div><label className="wizard-custom-currency">Another currency<CurrencySelect required value={f.currency} onChange={currency=>setF({...f,currency})}/></label></section>}
            {accountStep === 4 && <section><h3>What kind of account is it?</h3><p className="muted">Choose what this account represents. Stocks and funds are holdings inside an investment account—not separate accounts.</p><div className="wizard-choice-grid compact">{ACCOUNT_TYPES.map(([value,label,description])=><button type="button" key={value} className={f.accountType===value?'choice-card active':'choice-card'} onClick={()=>setF({...f,accountType:value,isLiability:['credit_card','loan','mortgage'].includes(value)})}><b>{label}</b><small>{description}</small></button>)}</div>{f.accountType==='investment'&&<Notice tone="info"><b>One broker account, many holdings.</b> Create the brokerage account here. On the next page, use <b>Add holding / transaction</b> for each stock, ETF, fund or bond.</Notice>}<div className="form-grid"><label>Account name<input autoFocus required value={f.name} onChange={e=>setF({...f,name:e.target.value})} placeholder={f.accountType==='investment'?'e.g. Interactive Brokers':'e.g. Everyday account'}/></label></div><button type="button" className="text-button" onClick={()=>setAccountMore(!accountMore)}>{accountMore?'Hide optional details':'Add more details'}</button>{accountMore&&<div className="form-grid wizard-more"><label>Account identifier<input value={f.identifier} onChange={e=>setF({...f,identifier:e.target.value})} placeholder="Stored encrypted"/></label><label>Opening balance<input type="number" step="0.01" value={f.openingBalance} onChange={e=>setF({...f,openingBalance:e.target.value})}/><small>{f.accountType==='investment'?'Cash held at the broker only; do not enter the value of stocks here.':'Balance before imported movements.'}</small></label><label className="check"><input type="checkbox" checked={f.includeInTotals} onChange={e=>setF({...f,includeInTotals:e.target.checked})}/>Include in totals <Info size={13}/></label><label className="check"><input type="checkbox" checked={f.isLiability} onChange={e=>setF({...f,isLiability:e.target.checked})}/>Treat as money owed <Info size={13}/></label></div>}</section>}
            {accountStep === 5 && <section><h3>Review your account</h3><div className="wizard-review"><div><small>Cost center</small><b>{cfg.costCenters.find(x=>String(x.id)===String(f.costCenterId))?.name||'Not assigned'}</b></div><div><small>Place</small><b>{f.locationId==='new'?f.newLocationName:cfg.locations.find(x=>String(x.id)===String(f.locationId))?.name||'Not assigned'}</b></div><div><small>Currency</small><b>{f.currency}</b></div><div><small>Account</small><b>{f.name||'Unnamed'} · {accountTypeLabel(f.accountType)}</b></div>{accountMore&&<><div><small>Opening balance</small><b>{money(f.openingBalance||0,f.currency)}</b></div><div><small>Totals</small><b>{f.includeInTotals?'Included':'Excluded'}{f.isLiability?' · money owed':''}</b></div></>}</div>{f.accountType==='investment'&&<p className="muted">After creation, Pisto will open this brokerage account so you can add its individual holdings and historical purchases.</p>}</section>}
            <div className="wizard-nav"><button type="button" className="secondary" onClick={()=>{setWizardError('');accountStep===1?closeAccountWizard():setAccountStep(x=>Math.max(1,x-1))}}>{accountStep===1?'Cancel':<><ChevronLeft size={16}/> Back</>}</button>{accountStep<5?<button type="button" className="primary" disabled={(accountStep===2&&(!f.locationId||(f.locationId==='new'&&!f.newLocationName.trim())))||(accountStep===4&&!f.name.trim())} onClick={nextAccountStep}>Next <ChevronRight size={16}/></button>:<button className="primary">Create account</button>}</div>
          </form>
        </Modal>
      )}
    </>
  );
}
